import type { UserResponse } from 'stream-chat';
import { StreamClient, UserFilterArgs, UserFilterSort } from '../../submodules/s2-chat-compose-search/src/types';
import { filterSelf } from '../../submodules/s2-chat-compose-search/src/util/filterSelf';

const PAGE_SIZE = 100;
// queryUsers rejects an offset above 1000.
const MAX_OFFSET = 1000;
const OR_CLAUSE_LIMIT = 100;

export async function getManagerContacts(
    streamClient: StreamClient,
    query?: string
): Promise<UserResponse[]> {
    if (!streamClient?.user?._location
        || !streamClient?.user?.teams?.length) {
        return [];
    }
    try {
        const searchArgs: UserFilterArgs = {
            teams: { $contains: streamClient.user?.teams[0] },
        };
        if (query) searchArgs.name = { $autocomplete: query };

        const collected: UserResponse[] = [];
        // Name sort keeps offset pages stable. An empty sort does not.
        for (let offset = 0; offset <= MAX_OFFSET; offset += PAGE_SIZE) {
            const queryUsersResponse = await streamClient.queryUsers(searchArgs, { name: 1 }, {
                limit: PAGE_SIZE,
                offset,
            });
            const page = queryUsersResponse.users;
            collected.push(...page);
            if (page.length < PAGE_SIZE) break;
        }

        // Stream rejects $contains on custom user fields; match managed locations client-side.
        const myLocation = streamClient.user._location;
        const managers = collected.filter((user) =>
            Array.isArray(user._manager_for_locations)
            && user._manager_for_locations.includes(myLocation)
        );

        return filterSelf(managers, streamClient.user.id);
    } catch (error: any) {
        throw new Error(error);
    }
}

// Stream only supports $eq on custom user fields.
async function queryUsersAtLocations(
    streamClient: StreamClient,
    baseFilter: UserFilterArgs,
    sort: UserFilterSort,
    locations: string[],
): Promise<UserResponse[]> {
    const chunks: string[][] = [];
    for (let i = 0; i < locations.length; i += OR_CLAUSE_LIMIT) {
        chunks.push(locations.slice(i, i + OR_CLAUSE_LIMIT));
    }

    const responses = await Promise.all(chunks.map((chunk) => {
        const filter: UserFilterArgs = { ...baseFilter };
        if (chunk.length === 1) {
            filter._location = { $eq: chunk[0] };
        } else {
            filter.$or = chunk.map((location) => ({
                _location: { $eq: location },
            })) as unknown as NonNullable<UserFilterArgs['$or']>;
        }
        return streamClient.queryUsers(filter, sort, { limit: PAGE_SIZE });
    }));

    const users: UserResponse[] = [];
    const seen: { [id: string]: true } = {};
    for (const response of responses) {
        for (const user of response.users) {
            if (seen[user.id]) continue;
            seen[user.id] = true;
            users.push(user);
        }
    }

    users.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return users;
}

export async function getLocationContacts(
    streamClient: StreamClient,
    query?: string
): Promise<UserResponse[]> {
    let enable_location_search = false;
    let locations_to_search: string[] = [];
    let enable_crew_chat = false;

    if (!streamClient?.user?._location
        || !streamClient?.user?.teams?.length) {
        return [];
    }

    if (Array.isArray(streamClient.user._manager_for_locations)
        && streamClient.user._manager_for_locations.length > 0) {
        enable_crew_chat = true;
    }

    switch (streamClient.user._chat_options) {
        case 'Crew to Crew Disabled (allow Office)':
            if (streamClient.user._location === 'Office') {
                enable_crew_chat = true;
            }
            break;
        case 'Crew to Crew Enabled (within location)':
            enable_crew_chat = true;
            break;
        case 'Crew to Crew Disabled (allow Office and Managers)': {
            const {
                _manager_for_locations,
                _location
            } = streamClient.user;

            if (_location === 'Office') {
                enable_crew_chat = true;
                enable_location_search = true;
            }

            if (Array.isArray(_manager_for_locations) && _location !== 'Office') {
                enable_crew_chat = true;
                enable_location_search = true;
                locations_to_search = _manager_for_locations;
            }
            break;
        }
        case 'Crew to Crew Enabled (across locations)':
            enable_location_search = true;
            enable_crew_chat = true;
            break;
        default:
            break;
    }

    if (streamClient.user._admin || streamClient.user._hr) {
        enable_location_search = true;
        enable_crew_chat = true;
        locations_to_search = [];
    }

    if (enable_crew_chat) {
        try {
            const searchArgs: UserFilterArgs = {
                teams: { $contains: streamClient.user?.teams[0] },
            };
            let locationsForOr: string[] | null = null;

            if (query) {
                searchArgs.name = { $autocomplete: query };
                if (!enable_location_search) {
                    searchArgs._location = { $eq: streamClient.user?._location };
                } else if (locations_to_search.length > 0) {
                    locationsForOr = locations_to_search;
                }
            } else searchArgs._location = { $eq: streamClient.user?._location };

            let sort: UserFilterSort;
            if (query) sort = { name: 1 };
            else sort = [{ _location: 1 }, { name: 1 }];

            if (locationsForOr) {
                const users = await queryUsersAtLocations(streamClient, searchArgs, sort, locationsForOr);
                return filterSelf(users, streamClient.user.id);
            }

            const queryUsersResponse = await streamClient.queryUsers(searchArgs, sort, { limit: 100 });
            queryUsersResponse.users = filterSelf(queryUsersResponse.users, streamClient.user.id);

            return queryUsersResponse.users;
        } catch (error: any) {
            throw new Error(error);
        }
    } else {
        return [];
    }
}
